import { keyBy } from "lodash-es";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom-v5-compat";

import {
  Button,
  LinkButton,
  Price,
  ResponsivePrimaryAction,
  ResponsiveTitleBar,
} from "../../Atoms";
import { useStartTabCheckoutSession } from "../Checkout/useStartTabCheckoutSession";
import { useLoadedCustomer } from "../Customer/queries";
import { Customer } from "../Customer/schema";
import { useLoadedCustomerTab } from "../CustomerTab/queries";
import { OrderItem } from "../CustomerTab/schema";
import { OrderItemSwitch, RemainderClaim } from "./ClaimedContent";
import { MissingCustomer } from "./MissingCustomer";
import {
  useCreateOrderItemClaim,
  useDeleteOrderItemClaim,
} from "./OrderItemClaims/queries";
import { Pill } from "./Pill";
import { CustomerPillList } from "./Pill";
import { useLoadedBillSplit, useClaimRemainder } from "./queries";
import { BillSplit, OrderItemClaim, OrderItemStatus } from "./schema";
import {
  getClaimedOrderItemsIn,
  getOrderItemsTotal,
  getTotalRemainingUnclaimed,
  hasCustomerPaidIn,
} from "./selectors";
import { getPaidAtTillAmount } from "./selectors";

export const OrderItemsClaim = ({}: {}) => {
  const { t } = useTranslation();
  const { data: customerTab } = useLoadedCustomerTab();
  const { data: billSplit } = useLoadedBillSplit();
  const { data: customer } = useLoadedCustomer();

  const claimRemainder = useClaimRemainder(billSplit);
  const createCreateOrderItemClaim = useCreateOrderItemClaim(billSplit);
  const deleteOrderItemClaimMutation = useDeleteOrderItemClaim(billSplit);
  const claimItem = (orderItemUuid: string) => async () => {
    createCreateOrderItemClaim.mutate(orderItemUuid);
  };

  const deleteOrderItemClaim = (orderItemClaim: OrderItemClaim) => async () => {
    deleteOrderItemClaimMutation.mutate(orderItemClaim);
  };

  const { startTabCheckoutSession, submissionState: checkoutSubmissionState } =
    useStartTabCheckoutSession();

  const orderItems = customerTab?.orders.flatMap((o) =>
    o.orderItems.filter((oi) => !oi.voided),
  );
  const orderItemClaims = keyBy(billSplit.orderItemClaims, "orderItemId");
  const customers = keyBy(billSplit.customers, "uuid");
  const getClaimedOrderItemsFor = getClaimedOrderItemsIn(
    billSplit,
    customerTab,
  );
  const yourOrderItems = getClaimedOrderItemsFor(customer);
  const claimWithCustomer = (claim?: OrderItemClaim) => {
    if (!claim) {
      return undefined;
    }
    return {
      ...claim,
      customer: customers[claim.customerId] || MissingCustomer,
    };
  };

  const onClaimRemainder = (customer: Customer | null) => {
    claimRemainder.mutate(customer);
  };

  const getHasCustomerPaid = hasCustomerPaidIn(billSplit);
  const yourOrderItemsTotal = getOrderItemsTotal(yourOrderItems);
  const unclaimedTotal = getTotalRemainingUnclaimed(billSplit, customerTab);
  const isClaimingRemainder = billSplit.remainderClaimerId === customer.uuid;
  const toPay =
    yourOrderItemsTotal + (isClaimingRemainder ? unclaimedTotal : 0);
  const hasPaid = getHasCustomerPaid(customer);
  const getOrderItemStatus = (orderItem: OrderItem): OrderItemStatus => {
    if (orderItem.paymentUuid) {
      if (orderItemClaims[orderItem.uuid]?.customerId) {
        return "others_paid";
      }
      return "paid_at_till";
    }
    if (orderItemClaims[orderItem.uuid]?.customerId === customer.uuid) {
      return "yours";
    }
    if (orderItemClaims[orderItem.uuid]?.customerId) {
      return "others_claimed";
    }
    return "unclaimed";
  };

  const paidAtTillAmount = getPaidAtTillAmount(billSplit, customerTab);
  return (
    <div className="mx-auto max-w-screen-sm space-y-32 pb-32">
      <ResponsiveTitleBar>
        {t("billSplit.selectYourItemsTitle")}
      </ResponsiveTitleBar>
      {hasPaid && (
        <div className="rounded-venue-brand border-2 border-venue-highlight bg-venue-tertiary p-12">
          <div className="font-semibold">{t("billSplit.youHavePaid")}</div>
          <div className="">{t("billSplit.youHavePaidDescription")}</div>
        </div>
      )}
      <div className="space-y-24">
        <div className="">
          <h3 className="text-xl font-bold">
            {t("billSplit.customersOnBillSplit")}
          </h3>
        </div>
        <CustomerPillList
          billSplit={billSplit}
          customerTab={customerTab}
          currentCustomer={customer}
          inline
        />
        <div className="flex items-center justify-between">
          <Link to="share">
            <Pill>{t("billSplit.shareWithOthers")} </Pill>
          </Link>
        </div>
      </div>
      <div className="">
        <h3 className="text-xl font-bold">
          {t("billSplit.selectItemsForPayment")}
        </h3>
        {customerTab.totalHolidaySurcharges.length > 0 && (
          <div className="">{t("billSplit.surchargeWarning")}</div>
        )}
      </div>
      {false && (
        <PreviewContent
          orderItem={orderItems[orderItems.length - 1]}
          claim={claimWithCustomer(
            billSplit.orderItemClaims[billSplit.orderItemClaims.length - 1],
          )}
          billSplit={billSplit}
        />
      )}
      <div className="space-y-8">
        {orderItems.map((orderItem) => (
          <OrderItemSwitch
            key={orderItem.uuid}
            orderItem={orderItem}
            status={getOrderItemStatus(orderItem)}
            disabled={hasPaid}
            unclaimable={unclaimedTotal < orderItem.totalSalesPriceTaxInclusive}
            billSplit={billSplit}
            claim={claimWithCustomer(orderItemClaims[orderItem.uuid])}
            deleteOrderItemClaim={deleteOrderItemClaim(
              orderItemClaims[orderItem.uuid],
            )}
            claimItem={claimItem(orderItem.uuid)}
          />
        ))}
      </div>
      {paidAtTillAmount > 0 && (
        <h3 className="text-xl font-bold">
          {t("billSplit.paidAtTillAmount")}{" "}
          <Price showCurrency value={paidAtTillAmount} />
        </h3>
      )}
      <div>
        {unclaimedTotal > 0 ? (
          <RemainderClaim
            billSplit={billSplit}
            customer={customer}
            onClaimRemainder={onClaimRemainder}
            unclaimedTotal={unclaimedTotal}
          />
        ) : (
          <div className="text-center text-lg font-semibold">
            {" "}
            {t("billSplit.noRemainderLeftToClaim")}{" "}
          </div>
        )}
      </div>
      <div className="text-center italic">
        {t("billSplit.anyIssuesPayAtTheTill")}
      </div>
      <Button secondary onClick={startTabCheckoutSession}>
        {t("billSplit.payItAll")}
      </Button>

      <ResponsivePrimaryAction>
        {!hasPaid ? (
          <LinkButton primary to={`./review`}>
            {t("billSplit.readyForPayment")}{" "}
            <Price showCurrency value={toPay} />
          </LinkButton>
        ) : (
          <Button secondary disabled>
            {t("billSplit.youHavePaid")}
          </Button>
        )}
      </ResponsivePrimaryAction>
    </div>
  );
};

const PreviewContent = ({
  billSplit: initialBillSplit,
  orderItem,
  claim,
}: {
  billSplit: BillSplit;
  orderItem: OrderItem;
  claim?: OrderItemClaim & { customer: Customer };
}) => {
  const owner: Customer = {
    uuid: "owner",
    nickname: "Owner",
    icon: "croissant",
    createdAt: "",
  };
  const anotherCustomer: Customer = {
    uuid: "customer",
    nickname: "Customer",
    icon: "burger",
    createdAt: "",
  };

  const billSplit = {
    ...initialBillSplit,
    customers: [...initialBillSplit.customers, owner, anotherCustomer],
  };
  return (
    <div className="-m-12 space-y-16 rounded-venue-brand border-4 border-venue-primary p-12">
      <div className="text-xl font-bold">Preview Claimable content</div>
      <div className="space-y-12">
        <div className="text-lg font-bold">Your Content</div>
        <OrderItemSwitch
          status="yours"
          orderItem={orderItem}
          claimItem={function (): void {
            throw new Error("Function not implemented.");
          }}
          billSplit={billSplit}
          deleteOrderItemClaim={function (): void {
            throw new Error("Function not implemented.");
          }}
        />
      </div>{" "}
      <div className="space-y-8">
        <div className="text-lg font-bold">Their Claimed Content</div>
        {claim ? (
          <OrderItemSwitch
            status="others_claimed"
            orderItem={orderItem}
            claim={{ ...claim, customer: anotherCustomer }}
            claimItem={function (): void {
              throw new Error("Function not implemented.");
            }}
            billSplit={billSplit}
            deleteOrderItemClaim={function (): void {
              throw new Error("Function not implemented.");
            }}
          />
        ) : (
          <div> Missing a claim for this content </div>
        )}
      </div>
      <div className="space-y-8">
        <div className="text-lg font-bold">Their Paid Content</div>
        {claim ? (
          <OrderItemSwitch
            status="others_paid"
            orderItem={orderItem}
            claim={{ ...claim, customer: anotherCustomer }}
            claimItem={function (): void {
              throw new Error("Function not implemented.");
            }}
            billSplit={billSplit}
            deleteOrderItemClaim={function (): void {
              throw new Error("Function not implemented.");
            }}
          />
        ) : (
          <div> Missing a claim to show this content </div>
        )}
      </div>
      <div className="space-y-8">
        <div className="text-lg font-bold">Paid at till</div>
        <OrderItemSwitch
          status="paid_at_till"
          orderItem={orderItem}
          claim={claim}
          claimItem={function (): void {
            throw new Error("Function not implemented.");
          }}
          billSplit={billSplit}
          deleteOrderItemClaim={function (): void {
            throw new Error("Function not implemented.");
          }}
        />
      </div>
      <div className="space-y-8">
        <div className="text-lg font-bold">Unclaimed</div>
        <OrderItemSwitch
          status="unclaimed"
          orderItem={orderItem}
          claim={claim}
          claimItem={function (): void {
            throw new Error("Function not implemented.");
          }}
          billSplit={billSplit}
          deleteOrderItemClaim={function (): void {
            throw new Error("Function not implemented.");
          }}
        />
      </div>
      <div className="space-y-8">
        <div className="text-lg font-bold">Unclaimable</div>
        <OrderItemSwitch
          status="unclaimed"
          unclaimable
          orderItem={orderItem}
          claim={claim}
          claimItem={function (): void {
            throw new Error("Function not implemented.");
          }}
          billSplit={billSplit}
          deleteOrderItemClaim={function (): void {
            throw new Error("Function not implemented.");
          }}
        />
      </div>
      <div className="space-y-8">
        <div className="text-lg font-bold">
          Unclaimed Remainder Claim (for owner)
        </div>
        <RemainderClaim
          customer={owner}
          unclaimedTotal={43.21}
          onClaimRemainder={() => {}}
          billSplit={{
            ...billSplit,
            remainderClaimerId: null,
            ownerId: owner.uuid,
          }}
        />
      </div>
      <div className="space-y-8">
        <div className="text-lg font-bold">
          Unclaimed Remainder Claim (for others)
        </div>
        <RemainderClaim
          customer={anotherCustomer}
          unclaimedTotal={43.21}
          onClaimRemainder={() => {}}
          billSplit={{ ...billSplit, remainderClaimerId: null }}
        />
      </div>
      <div className="space-y-8">
        <div className="text-lg font-bold">Your Claimed Remainder Claim</div>
        <RemainderClaim
          customer={anotherCustomer}
          unclaimedTotal={43.21}
          onClaimRemainder={() => {}}
          billSplit={{ ...billSplit, remainderClaimerId: anotherCustomer.uuid }}
        />
      </div>
      <div className="space-y-8">
        <div className="text-lg font-bold">
          Claimed Remainder Claim (for others)
        </div>
        <RemainderClaim
          customer={owner}
          unclaimedTotal={43.21}
          onClaimRemainder={() => {}}
          billSplit={{ ...billSplit, remainderClaimerId: anotherCustomer.uuid }}
        />
      </div>
    </div>
  );
};
export const BillSplitSelectOrderItems = ({}) => {
  const { t } = useTranslation();
  return (
    <div>
      <OrderItemsClaim />
    </div>
  );
};
